/**
 * The narrator, as ai-tutor-v2 builds it.
 *
 * public/ai-tutor/narrator-engine.html is ai-tutor-v2's narrator engine
 * (`_NARRATOR_JS` in backend/app/routers/guide.py), copied verbatim — the same
 * file the student view serves. Keep it that way: when the engine changes
 * there, copy it here. The reading order, the junction pauses and the speech
 * normalisation all decide *which text* is spoken, and every narration wav is
 * filed under a hash of exactly that text, so a second engine means a course
 * that reads in silence.
 *
 * Two things are adapted around it, without editing it:
 *
 *  - The engine asks for each line's audio by POSTing the text to a narrate
 *    URL. A chapter here is a srcdoc iframe, and the backend is on another
 *    origin behind a JWT, so a shim installed before the engine catches that
 *    request and hands the text to the reader (`window.__aiTutorNarrate`),
 *    which resolves it through the backend to a signed URL and returns the wav.
 *  - The session placeholder is filled with a constant: the shim, not the URL,
 *    knows the session.
 *
 * And one thing is added after it: READER_REPORTS, which tells the reader what
 * the engine does not — the sections in view, and the section a highlight is
 * in — so the tutor knows what "this" means when nothing is being narrated.
 */

const ENGINE_URL = `${process.env.PUBLIC_URL || ""}/ai-tutor/narrator-engine.html`;
const NARRATE_PATH = "/__ai_tutor/narrate";

let enginePromise = null;

/** The engine's <script> block, ready to inject. Fetched once per page load. */
export const loadEngine = () => {
  if (!enginePromise) {
    enginePromise = fetch(ENGINE_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`narrator engine missing (${r.status})`);
        return r.text();
      })
      .then((text) =>
        text
          .replace("/api/v1/voice/SESSION_ID_PLACEHOLDER/narrate", NARRATE_PATH)
          .replace(/SESSION_ID_PLACEHOLDER/g, "current")
      )
      .catch((e) => {
        enginePromise = null; // let the next chapter try again
        throw e;
      });
  }
  return enginePromise;
};

// Runs inside the chapter iframe, before anything else. The iframe is
// same-origin (sandbox allow-same-origin), so it can call the reader directly.
const SHIM = `<script>(function () {
  var realFetch = window.fetch;
  window.fetch = function (url, opts) {
    if (typeof url === "string" && url.indexOf("${NARRATE_PATH}") === 0) {
      var text = "";
      try { text = JSON.parse((opts && opts.body) || "{}").text || ""; } catch (e) {}
      var narrate = window.parent && window.parent.__aiTutorNarrate;
      if (!narrate) return Promise.resolve(new Response(null, { status: 503 }));
      return narrate(text).then(function (blob) {
        return blob ? new Response(blob, { status: 200, headers: { "Content-Type": "audio/wav" } })
                    : new Response(null, { status: 404 });
      }, function () { return new Response(null, { status: 502 }); });
    }
    return realFetch.apply(this, arguments);
  };
})();</script>`;

// Runs inside the chapter iframe, after the engine. The engine posts
// `text_selected` with the text only; this posts `text_section` for the same
// selection with the outermost data-section it is in (the id the tutor knows),
// and `viewport_sections`: the outermost sections in view, nearest the middle
// first, when they change — at most every 700 ms while scrolling.
export const READER_REPORTS = `<script id="reader-reports">(function () {
  function post(m) { try { window.parent.postMessage(m, '*'); } catch (e) {} }
  function sectionOf(node) {
    var id = '';
    while (node && node !== document.body && node !== document) {
      if (node.nodeType === 1 && node.hasAttribute('data-section')) id = node.getAttribute('data-section') || id;
      node = node.parentNode;
    }
    return id;
  }
  document.addEventListener('mouseup', function () {
    var sel = window.getSelection && window.getSelection();
    var text = sel ? sel.toString().trim() : '';
    if (text && sel.rangeCount) {
      post({ type: 'text_section', text: text, section: sectionOf(sel.getRangeAt(0).commonAncestorContainer) });
    }
  });
  var last = null;
  function report() {
    var h = window.innerHeight || document.documentElement.clientHeight;
    var found = [];
    var els = document.querySelectorAll('[data-section]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.parentElement && el.parentElement.closest('[data-section]')) continue;
      var id = el.getAttribute('data-section');
      var r = el.getBoundingClientRect();
      if (!id || r.height === 0 || r.bottom <= 0 || r.top >= h) continue;
      found.push({ id: id, d: Math.abs((Math.max(r.top, 0) + Math.min(r.bottom, h)) / 2 - h / 2) });
    }
    found.sort(function (a, b) { return a.d - b.d; });
    var ids = found.slice(0, 4).map(function (f) { return f.id; });
    if (ids.join(',') === last) return;
    last = ids.join(',');
    post({ type: 'viewport_sections', sections: ids });
  }
  var timer = null;
  function soon() { if (!timer) timer = setTimeout(function () { timer = null; report(); }, 700); }
  window.addEventListener('scroll', soon, { passive: true });
  window.addEventListener('resize', soon);
  window.addEventListener('load', report);
  setTimeout(report, 300);
})();</script>`;

/**
 * The section a selection is in, from the reader's two messages about it,
 * whichever arrives first: the engine's `text_selected` (text only) and
 * READER_REPORTS' `text_section` (text and section).
 */
export const sectionForSelection = (text, reported) =>
  (reported && reported.text === text ? reported.section || "" : "");

/** The chapter with the shim at the top of <head>, and the engine and READER_REPORTS at the end of <body>. */
export const withEngine = (html, engine) => {
  let out = html || "";
  if (/<head[^>]*>/i.test(out)) {
    out = out.replace(/<head[^>]*>/i, (m) => `${m}\n${SHIM}`);
  } else {
    out = `${SHIM}\n${out}`;
  }
  if (out.includes("</body>")) {
    // A function, not a string: in a replacement *string*, "$&", "$\`" and "$'"
    // are patterns, and the engine's source contains them — it would splice
    // copies of the page into itself.
    return out.replace("</body>", () => `${engine}\n${READER_REPORTS}\n</body>`);
  }
  return `${out}\n${engine}\n${READER_REPORTS}`;
};

/**
 * Resolves narration text → wav blob through the backend, with one batched
 * lookup per chapter. Install with `narration.install(sessionId)`; feed it the
 * chapter's lines when the engine reports them (`prefetch(texts)`).
 */
export const createNarration = (getNarrationUrls) => {
  const urls = new Map(); // trimmed text → signed URL or null
  let sessionId = null;

  const lookup = async (texts) => {
    const wanted = [...new Set(texts.map((t) => (t || "").trim()).filter((t) => t && !urls.has(t)))];
    for (let i = 0; i < wanted.length; i += 500) {
      const batch = wanted.slice(i, i + 500);
      const data = await getNarrationUrls(sessionId, batch);
      batch.forEach((t, j) => urls.set(t, data.urls?.[data.keys?.[j]] || null));
    }
  };

  const narrate = async (text) => {
    const key = (text || "").trim();
    if (!key || !sessionId) return null;
    if (!urls.has(key)) {
      try { await lookup([key]); } catch { return null; }
    }
    const url = urls.get(key);
    if (!url) return null; // this line shipped without audio: the engine moves on
    const resp = await fetch(url);
    return resp.ok ? resp.blob() : null;
  };

  return {
    install(id) {
      sessionId = id;
      window.__aiTutorNarrate = narrate;
    },
    uninstall() {
      if (window.__aiTutorNarrate === narrate) delete window.__aiTutorNarrate;
      urls.clear();
    },
    prefetch(texts) {
      return lookup(texts || []).catch(() => {});
    },
  };
};
